// CB:BEGIN admin-edit-homeowner-dashboard
import React, { useState, useEffect } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SharedHeaderAdmin from '../../components/SharedHeaderAdmin';
import SettingsCard from '../../components/ui/SettingsCard';
import { useApi } from '@/lib/useApi';

interface AdminEditHomeownerDashboardProps {
  homeownerId: string;
  onNavigate?: (screen: string) => void;
}

interface Homeowner {
  id: string;
  firstName: string;
  lastName: string;
  address: string;
  city?: string;
  state?: string;
  zipCode?: string;
  textOptIn: boolean;
  user: {
    email: string;
    phone?: string;
  };
  projects: Project[];
}

interface Project {
  id: string;
  address: string;
  status: string;
  projectType: string;
  desiredStart?: string;
  roofScopeOnly: boolean;
  isPartial: boolean;
  extraInfo?: string;
  acceptedContractorName?: string;
  acceptedBidCost?: number;
  createdAt: string;
  updatedAt: string;
  bids: Bid[];
  manualBids: ManualBid[];
  phases: ProjectPhase[];
  escrowAccount?: {
    currentBalance: number;
    totalDeposited: number;
    totalReleased: number;
  };
}

interface Bid {
  id: string;
  projectId: string;
  contractorId?: string;
  amount?: number;
  amountCents?: number;
  note?: string;
  status?: string;
  isAccepted: boolean;
  isFake: boolean;
  createdAt: string;
  contractorEmail?: string;
  contractorName?: string;
  contractorPhone?: string;
  contractor?: {
    company?: string;
    user: {
      email: string;
    };
  };
}

interface ManualBid {
  id: string;
  amountCents: number;
  contractorName: string;
  contractorEmail: string;
  contractorPhone?: string;
  note?: string;
  createdAt: string;
}

interface ProjectPhase {
  id: string;
  number: number;
  title?: string;
  status: string;
  percentOfTotal?: number;
  amountCents?: number;
  contractorSubmittedAt?: string;
  homeownerAcceptedAt?: string;
  depositReleasedAt?: string;
  completedAt?: string;
}

const AdminEditHomeownerDashboard: React.FC<AdminEditHomeownerDashboardProps> = ({ 
  homeownerId, 
  onNavigate 
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [homeowner, setHomeowner] = useState<Homeowner | null>(null);

  // Dashboard-relevant state (project information only)
  const [projectStatus, setProjectStatus] = useState('');
  const [projectType, setProjectType] = useState('');
  const [desiredStart, setDesiredStart] = useState('');
  const [roofScopeOnly, setRoofScopeOnly] = useState(false);
  const [isPartial, setIsPartial] = useState(false);
  const [extraInfo, setExtraInfo] = useState('');
  const [acceptedContractorName, setAcceptedContractorName] = useState('');
  const [acceptedBidCost, setAcceptedBidCost] = useState('');

  // Bids state
  const [bids, setBids] = useState<Bid[]>([]);
  const [manualBids, setManualBids] = useState<ManualBid[]>([]);
  
  // Individual bid editing state
  const [editingBid, setEditingBid] = useState<string | null>(null);
  const [bidAmountCents, setBidAmountCents] = useState('');
  const [bidNote, setBidNote] = useState('');
  const [bidStatus, setBidStatus] = useState('');
  const [bidIsAccepted, setBidIsAccepted] = useState(false);
  const [bidIsFake, setBidIsFake] = useState(false);
  const [bidContractorName, setBidContractorName] = useState('');
  const [bidContractorEmail, setBidContractorEmail] = useState('');
  const [bidContractorPhone, setBidContractorPhone] = useState('');

  // Add new bid state
  const [addingNewBid, setAddingNewBid] = useState(false);
  const [newBidContractorName, setNewBidContractorName] = useState('');
  const [newBidContractorEmail, setNewBidContractorEmail] = useState('');
  const [newBidContractorPhone, setNewBidContractorPhone] = useState('');
  const [newBidAmountCents, setNewBidAmountCents] = useState('');
  const [newBidNote, setNewBidNote] = useState('');
  const [newBidStatus, setNewBidStatus] = useState('PROPOSED');
  const [newBidIsAccepted, setNewBidIsAccepted] = useState(false);
  const [newBidIsFake, setNewBidIsFake] = useState(false);

  useEffect(() => {
    fetchHomeownerData();
  }, [homeownerId]);

  const fetchHomeownerData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/admin/homeowners/${homeownerId}`);
      const data = response; // Backend returns homeowner data directly
      
      setHomeowner(data);

      // Set dashboard-relevant project values (assuming first project)
      if (data.projects && data.projects.length > 0) {
        const project = data.projects[0];
        setProjectStatus(project.status || '');
        setProjectType(project.projectType || '');
        setDesiredStart(project.desiredStart ? project.desiredStart.split('T')[0] : '');
        setRoofScopeOnly(project.roofScopeOnly || false);
        setIsPartial(project.isPartial || false);
        setExtraInfo(project.extraInfo || '');
        setAcceptedContractorName(project.acceptedContractorName || '');
        setAcceptedBidCost(project.acceptedBidCost ? (project.acceptedBidCost / 100).toString() : '');
        
        setBids(project.bids || []);
        setManualBids(project.manualBids || []);
      }
    } catch (err) {
      console.error('Error fetching homeowner data:', err);
      setError('Failed to load homeowner data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Update project data (dashboard-relevant information)
      if (homeowner?.projects && homeowner.projects.length > 0) {
        const projectId = homeowner.projects[0].id;
        await api.put(`/api/admin/projects/${projectId}`, {
          status: projectStatus,
          projectType,
          desiredStart: desiredStart ? new Date(desiredStart).toISOString() : null,
          roofScopeOnly,
          isPartial,
          extraInfo,
          acceptedContractorName,
          acceptedBidCost: acceptedBidCost ? Math.round(parseFloat(acceptedBidCost) * 100) : null
        });
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchHomeownerData(); // Refresh data
      }, 2000);
    } catch (err) {
      console.error('Error saving dashboard data:', err);
      setError('Failed to save dashboard changes');
    } finally {
      setSaving(false);
    }
  };

  const handleBidAccept = async (bidId: string) => {
    try {
      if (!homeowner?.projects || homeowner.projects.length === 0) return;
      
      const projectId = homeowner.projects[0].id;
      
      // First, reject all accepted bids for this project
      const allBids = [...bids, ...manualBids];
      for (const bid of allBids) {
        if (bid.isAccepted && bid.id !== bidId) {
          await api.put(`/api/admin/bids/${bid.id}`, { isAccepted: false });
        }
      }
      
      // Then accept the new bid
      await api.post(`/api/projects/${projectId}/accept-bid`, { bidId });
      
      // Refresh data
      fetchHomeownerData();
    } catch (err) {
      console.error('Error accepting bid:', err);
      setError('Failed to accept bid');
    }
  };

  const handleEditBid = (bid: Bid) => {
    setEditingBid(bid.id);
    setBidAmountCents(bid.amountCents ? (bid.amountCents / 100).toString() : '');
    setBidNote(bid.note || '');
    setBidStatus(bid.status || '');
    setBidIsAccepted(bid.isAccepted);
    setBidIsFake(bid.isFake);
    setBidContractorName(bid.contractorName || '');
    setBidContractorEmail(bid.contractorEmail || '');
    setBidContractorPhone(bid.contractorPhone || '');
  };

  const handleSaveBid = async (bidId: string) => {
    try {
      setSaving(true);
      setError(null);

      await api.put(`/api/admin/bids/${bidId}`, {
        amountCents: bidAmountCents ? Math.round(parseFloat(bidAmountCents) * 100) : null,
        note: bidNote,
        status: bidStatus,
        isAccepted: bidIsAccepted,
        isFake: bidIsFake,
        contractorName: bidContractorName,
        contractorEmail: bidContractorEmail,
        contractorPhone: bidContractorPhone
      });

      setEditingBid(null);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchHomeownerData(); // Refresh data
      }, 2000);
    } catch (err) {
      console.error('Error saving bid:', err);
      setError('Failed to save bid changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditBid = () => {
    setEditingBid(null);
    setBidAmountCents('');
    setBidNote('');
    setBidStatus('');
    setBidIsAccepted(false);
    setBidIsFake(false);
    setBidContractorName('');
    setBidContractorEmail('');
    setBidContractorPhone('');
  };

  const handleDeleteBid = async (bidId: string) => {
    if (!confirm('Are you sure you want to delete this bid? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await api.delete(`/api/admin/bids/${bidId}`);

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchHomeownerData(); // Refresh data
      }, 2000);
    } catch (err) {
      console.error('Error deleting bid:', err);
      setError('Failed to delete bid');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewBid = () => {
    setAddingNewBid(true);
    // Reset form fields
    setNewBidContractorName('');
    setNewBidContractorEmail('');
    setNewBidContractorPhone('');
    setNewBidAmountCents('');
    setNewBidNote('');
    setNewBidStatus('PROPOSED');
    setNewBidIsAccepted(false);
    setNewBidIsFake(false);
  };

  const handleSaveNewBid = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate required fields
      if (!newBidContractorName || !newBidContractorEmail) {
        throw new Error('Contractor name and email are required');
      }

      // Get the first project ID (assuming homeowner has at least one project)
      const projectId = homeowner?.projects?.[0]?.id;
      if (!projectId) {
        throw new Error('No project found for this homeowner');
      }

      await api.post('/api/admin/bids', {
        projectId,
        contractorName: newBidContractorName,
        contractorEmail: newBidContractorEmail,
        contractorPhone: newBidContractorPhone || null,
        amountCents: newBidAmountCents ? Math.round(parseFloat(newBidAmountCents) * 100) : null,
        note: newBidNote || null,
        status: newBidStatus,
        isAccepted: newBidIsAccepted,
        isFake: newBidIsFake
      });

      setAddingNewBid(false);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchHomeownerData(); // Refresh data
      }, 2000);
    } catch (err) {
      console.error('Error creating bid:', err);
      setError('Failed to create bid');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelNewBid = () => {
    setAddingNewBid(false);
    setNewBidContractorName('');
    setNewBidContractorEmail('');
    setNewBidContractorPhone('');
    setNewBidAmountCents('');
    setNewBidNote('');
    setNewBidStatus('PROPOSED');
    setNewBidIsAccepted(false);
    setNewBidIsFake(false);
  };

  if (loading) {
    return (
      <ResponsiveContainer className="container">
        <div className="content" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p style={{ marginTop: 16, color: '#6b7280' }}>Loading homeowner data...</p>
        </div>
      </ResponsiveContainer>
    );
  }

  if (!homeowner) {
    return (
      <ResponsiveContainer className="container">
        <div className="content" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p style={{ color: '#dc2626' }}>Homeowner not found</p>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer className="container">
      <div className="content">
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => onNavigate?.('admin-homeowners-list')}
              style={{ fontSize: 14, padding: '8px 16px' }}
            >
              ← Back to Homeowners
            </button>
            <h1 style={{ fontSize: 28, margin: 0 }}>
              Edit Dashboard - {homeowner.firstName} {homeowner.lastName}
            </h1>
            <div style={{ marginLeft: 'auto' }}>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary"
                style={{ 
                  fontSize: 16, 
                  padding: '8px 24px',
                  background: '#059669',
                  border: 'none',
                  borderRadius: 8,
                  color: 'white',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Dashboard Changes'}
              </button>
            </div>
          </div>
          <p style={{ color: '#6b7280', marginTop: 8 }}>
            Edit project information and manage bids that affect the homeowner's dashboard display
          </p>
        </div>

        {error && (
          <div style={{ 
            background: '#fee2e2', 
            border: '1px solid #fecaca', 
            borderRadius: 8, 
            padding: 16, 
            marginBottom: 24,
            color: '#dc2626'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {success && (
          <div style={{ 
            background: '#d1fae5', 
            border: '1px solid #a7f3d0', 
            borderRadius: 8, 
            padding: 16, 
            marginBottom: 24,
            color: '#065f46'
          }}>
            <strong>Success:</strong> Changes saved successfully!
          </div>
        )}

        {/* Project Information */}
        {homeowner.projects && homeowner.projects.length > 0 && (
          <SettingsCard title="Project Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Project Status
                </label>
                <select
                  value={projectStatus}
                  onChange={(e) => setProjectStatus(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                >
                  <option value="DRAFT">Draft</option>
                  <option value="BIDDING">Bidding</option>
                  <option value="BID_SELECTED">Bid Selected</option>
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Project Type
                </label>
                <select
                  value={projectType}
                  onChange={(e) => setProjectType(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                >
                  <option value="ROOF">Roof Replacement</option>
                  <option value="GUTTER">Gutter Installation</option>
                  <option value="SIDING">Siding</option>
                  <option value="WINDOWS">Windows</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Desired Start Date
                </label>
                <input
                  type="date"
                  value={desiredStart}
                  onChange={(e) => setDesiredStart(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Accepted Contractor Name
                </label>
                <input
                  type="text"
                  value={acceptedContractorName}
                  onChange={(e) => setAcceptedContractorName(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Accepted Bid Cost ($)
                </label>
                <input
                  type="number"
                  value={acceptedBidCost}
                  onChange={(e) => setAcceptedBidCost(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                  step="0.01"
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Additional Project Information
                </label>
                <textarea
                  value={extraInfo}
                  onChange={(e) => setExtraInfo(e.target.value)}
                  className="form-control"
                  rows={3}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20, marginTop: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={roofScopeOnly}
                  onChange={(e) => setRoofScopeOnly(e.target.checked)}
                />
                <span style={{ fontSize: 14, color: '#374151' }}>Roof scope only</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={isPartial}
                  onChange={(e) => setIsPartial(e.target.checked)}
                />
                <span style={{ fontSize: 14, color: '#374151' }}>Partial project</span>
              </label>
            </div>
          </SettingsCard>
        )}

        {/* Bids Management */}
        <SettingsCard title={`Contractor Bids (${bids.length})`}>
          {/* Add New Bid Button */}
          {!addingNewBid && (
            <div style={{ marginBottom: 16 }}>
              <button
                onClick={handleAddNewBid}
                className="btn btn-primary"
                style={{ fontSize: 14, padding: '10px 20px' }}
              >
                Add Bids +
              </button>
            </div>
          )}

          {/* Add New Bid Form */}
          {addingNewBid && (
            <div style={{ 
              border: '2px solid #3b82f6', 
              borderRadius: 8, 
              padding: 16,
              background: '#f0f9ff',
              marginBottom: 16
            }}>
              <h4 style={{ fontSize: 16, fontWeight: '600', margin: '0 0 16px 0', color: '#1e40af' }}>
                Add New Contractor Bid
              </h4>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                      Contractor Name *
                    </label>
                    <input
                      type="text"
                      value={newBidContractorName}
                      onChange={(e) => setNewBidContractorName(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      placeholder="Enter contractor name"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                      Amount ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={newBidAmountCents}
                      onChange={(e) => setNewBidAmountCents(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      placeholder="Enter bid amount"
                    />
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                      Contractor Email *
                    </label>
                    <input
                      type="email"
                      value={newBidContractorEmail}
                      onChange={(e) => setNewBidContractorEmail(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      placeholder="Enter contractor email"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                      Contractor Phone
                    </label>
                    <input
                      type="tel"
                      value={newBidContractorPhone}
                      onChange={(e) => setNewBidContractorPhone(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      placeholder="Enter contractor phone"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                    Status
                  </label>
                  <select
                    value={newBidStatus}
                    onChange={(e) => setNewBidStatus(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="PROPOSED">Proposed</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="WITHDRAWN">Withdrawn</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                    Note
                  </label>
                  <textarea
                    value={newBidNote}
                    onChange={(e) => setNewBidNote(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                    placeholder="Enter any additional notes"
                  />
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={newBidIsAccepted}
                      onChange={(e) => setNewBidIsAccepted(e.target.checked)}
                    />
                    <span style={{ fontSize: 14 }}>Accepted</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={newBidIsFake}
                      onChange={(e) => setNewBidIsFake(e.target.checked)}
                    />
                    <span style={{ fontSize: 14 }}>Fake Bid</span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCancelNewBid}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNewBid}
                    disabled={saving}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.6 : 1
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Bid'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Existing Bids */}
          {bids.length > 0 && (
            <div style={{ display: 'grid', gap: 16 }}>
              {bids.map((bid) => (
                <div key={bid.id} style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 8, 
                  padding: 16,
                  background: bid.isAccepted ? '#f0fdf4' : '#ffffff'
                }}>
                  {editingBid === bid.id ? (
                    // Edit Mode
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                            Contractor Name
                          </label>
                          <input
                            type="text"
                            value={bidContractorName}
                            onChange={(e) => setBidContractorName(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                            Amount ($)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={bidAmountCents}
                            onChange={(e) => setBidAmountCents(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                            Contractor Email
                          </label>
                          <input
                            type="email"
                            value={bidContractorEmail}
                            onChange={(e) => setBidContractorEmail(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                            Contractor Phone
                          </label>
                          <input
                            type="tel"
                            value={bidContractorPhone}
                            onChange={(e) => setBidContractorPhone(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                          Status
                        </label>
                        <select
                          value={bidStatus}
                          onChange={(e) => setBidStatus(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">Select Status</option>
                          <option value="PROPOSED">Proposed</option>
                          <option value="ACCEPTED">Accepted</option>
                          <option value="REJECTED">Rejected</option>
                          <option value="WITHDRAWN">Withdrawn</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                          Note
                        </label>
                        <textarea
                          value={bidNote}
                          onChange={(e) => setBidNote(e.target.value)}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            resize: 'vertical'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={bidIsAccepted}
                            onChange={(e) => setBidIsAccepted(e.target.checked)}
                          />
                          <span style={{ fontSize: 14 }}>Accepted</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={bidIsFake}
                            onChange={(e) => setBidIsFake(e.target.checked)}
                          />
                          <span style={{ fontSize: 14 }}>Fake Bid</span>
                        </label>
                      </div>

                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          onClick={handleCancelEditBid}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleDeleteBid(bid.id)}
                          disabled={saving}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            opacity: saving ? 0.6 : 1
                          }}
                        >
                          Delete Bid
                        </button>
                        <button
                          onClick={() => handleSaveBid(bid.id)}
                          disabled={saving}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            opacity: saving ? 0.6 : 1
                          }}
                        >
                          {saving ? 'Saving...' : 'Save Bid'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: 16, fontWeight: '600', margin: 0 }}>
                            {bid.contractorName || bid.contractor?.company || 'Unknown Contractor'}
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                            <div>
                              <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Email:</p>
                              <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                {bid.contractorEmail || bid.contractor?.user?.email || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Phone:</p>
                              <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                {bid.contractorPhone || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Amount:</p>
                              <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                ${bid.amountCents ? (bid.amountCents / 100).toFixed(2) : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Status:</p>
                              <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                {bid.status || 'N/A'}
                              </p>
                            </div>
                          </div>
                          {bid.note && (
                            <div style={{ marginTop: 8 }}>
                              <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Note:</p>
                              <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                {bid.note}
                              </p>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            {bid.isAccepted && (
                              <span style={{ 
                                fontSize: 12, 
                                padding: '2px 6px', 
                                backgroundColor: '#10b981', 
                                color: 'white', 
                                borderRadius: '4px' 
                              }}>
                                ACCEPTED
                              </span>
                            )}
                            {bid.isFake && (
                              <span style={{ 
                                fontSize: 12, 
                                padding: '2px 6px', 
                                backgroundColor: '#f59e0b', 
                                color: 'white', 
                                borderRadius: '4px' 
                              }}>
                                FAKE
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize: 12, color: '#9ca3af', margin: '8px 0 0 0' }}>
                            Created: {new Date(bid.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => handleEditBid(bid)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Edit
                          </button>
                          {!bid.isAccepted && (
                            <button
                              onClick={() => handleBidAccept(bid.id)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Accept
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteBid(bid.id)}
                            disabled={saving}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: saving ? 'not-allowed' : 'pointer',
                              opacity: saving ? 0.6 : 1
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </SettingsCard>

        {/* Manual Bids */}
        {manualBids.length > 0 && (
          <SettingsCard title={`Manual Bids (${manualBids.length})`}>
            <div style={{ display: 'grid', gap: 16 }}>
              {manualBids.map((bid) => (
                <div key={bid.id} style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 8, 
                  padding: 16,
                  background: '#f9fafb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <h4 style={{ fontSize: 16, fontWeight: '600', margin: 0 }}>
                        {bid.contractorName}
                      </h4>
                      <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                        {bid.contractorEmail}
                      </p>
                      <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                        Amount: ${(bid.amountCents / 100).toFixed(2)}
                      </p>
                      {bid.note && (
                        <p style={{ fontSize: 14, color: '#374151', margin: '4px 0' }}>
                          Note: {bid.note}
                        </p>
                      )}
                    </div>
                    <span style={{
                      fontSize: 12,
                      padding: '4px 8px',
                      borderRadius: 4,
                      background: '#fef3c7',
                      color: '#92400e'
                    }}>
                      Manual Bid
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </SettingsCard>
        )}

      </div>
    </ResponsiveContainer>
  );
};

export default AdminEditHomeownerDashboard;
// CB:END admin-edit-homeowner-dashboard
